// src/pages/SignupPage.jsx
import { useState } from "react";
import BookyaraLogo from "../components/ui/BookyaraLogo";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { UserPlus, Eye, EyeOff, Scissors, Check } from "lucide-react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";
import { useAuth } from "../context/AuthContext";
import GoogleButton from "../components/auth/GoogleButton";

const PASSWORD_RULES = [
  { test: (p) => p.length >= 8,    label: "At least 8 characters" },
  { test: (p) => /[A-Z]/.test(p), label: "One uppercase letter"  },
  { test: (p) => /[0-9]/.test(p), label: "One number"            },
];

export default function SignupPage() {
  const { signup, signInWithGoogle } = useAuth();
  const navigate   = useNavigate();
  const location   = useLocation();

  // e.g. /signup?tenant=acme-salon&redirect=/acme-salon/book
  const params      = new URLSearchParams(location.search);
  const tenantSlug  = params.get("tenant");
  const redirect    = params.get("redirect");

  const [form,    setForm]    = useState({ name: "", email: "", password: "", confirm: "" });
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw,  setShowPw]  = useState(false);

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  }

  async function resolveTenantId() {
    if (!tenantSlug) return null;
    const q    = query(collection(db, "tenants"), where("slug", "==", tenantSlug));
    const snap = await getDocs(q);
    return snap.empty ? null : snap.docs[0].id;
  }

  async function handleGoogle() {
    const tenantId = await resolveTenantId();
    await signInWithGoogle(tenantId);
    navigate(redirect || (tenantSlug ? `/${tenantSlug}` : "/"));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setError("Please fill in all fields."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }

    setLoading(true);
    try {
      const tenantId = await resolveTenantId();
      await signup(form.name.trim(), form.email, form.password, tenantId);

      // Redirect back to the page they came from, or the tenant home, or marketing
      navigate(redirect || (tenantSlug ? `/${tenantSlug}` : "/"));
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": "An account with this email already exists.",
        "auth/invalid-email":        "Please enter a valid email address.",
        "auth/weak-password":        "Password is too weak.",
      };
      setError(msgs[err.code] || "Sign up failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-100 rounded-full blur-3xl opacity-60" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-50 rounded-full blur-3xl opacity-80" />
      </div>

      <div className="relative w-full max-w-md animate-scale-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} className="inline-flex flex-col items-center group">
            <BookyaraLogo size="md" className="mb-4" />
          </button>
          <p className="text-ink-400 text-sm mt-1">
            {tenantSlug ? `Booking account for ${tenantSlug}` : "Online booking for small businesses"}
          </p>
        </div>

        <div className="auth-card">
          <h2 className="font-semibold text-xl text-ink-900 mb-1">Create your account</h2>
          <p className="text-ink-400 text-sm mb-6">Book appointments in seconds</p>

          <GoogleButton onClick={handleGoogle} label="Sign up with Google" />

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 border-t border-surface-200" />
            <span className="text-ink-400 text-xs">or sign up with email</span>
            <div className="flex-1 border-t border-surface-200" />
          </div>

          {error && <div className="alert-error mb-4">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Full Name</label>
              <input name="name" type="text" placeholder="John Doe"
                value={form.name} onChange={handleChange} className="form-input" />
            </div>

            <div>
              <label className="form-label">Email Address</label>
              <input name="email" type="email" placeholder="you@email.com"
                value={form.email} onChange={handleChange} className="form-input" autoComplete="email" />
            </div>

            <div>
              <label className="form-label">Password</label>
              <div className="relative">
                <input name="password" type={showPw ? "text" : "password"}
                  placeholder="Create a strong password"
                  value={form.password} onChange={handleChange}
                  className="form-input pr-11" autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-400 hover:text-brand-500 transition-colors">
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {form.password && (
                <div className="mt-2 space-y-1">
                  {PASSWORD_RULES.map(rule => (
                    <div key={rule.label} className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors
                        ${rule.test(form.password) ? "bg-brand-500" : "bg-surface-200"}`}>
                        {rule.test(form.password) && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>
                      <span className={`text-xs ${rule.test(form.password) ? "text-brand-600" : "text-ink-400"}`}>
                        {rule.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="form-label">Confirm Password</label>
              <input name="confirm" type="password" placeholder="Repeat your password"
                value={form.confirm} onChange={handleChange}
                className={`form-input ${form.confirm && form.confirm !== form.password ? "!border-red-300" : ""}`}
                autoComplete="new-password" />
            </div>

            <button type="submit" disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 mt-2">
              {loading
                ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <><UserPlus size={16} /> Create Account</>
              }
            </button>
          </form>

          <p className="text-center text-[11px] text-ink-400 mt-4 leading-relaxed">
            By creating an account, you agree to our{" "}
            <a href="/terms" className="text-brand-600 hover:underline">Terms of Service</a>{" "}and{" "}
            <a href="/privacy" className="text-brand-600 hover:underline">Privacy Policy</a>.
          </p>

          <p className="text-center text-sm text-ink-400 mt-6">
            Already have an account?{" "}
            <Link to={`/login${location.search}`}
              className="text-brand-600 hover:text-brand-700 font-semibold">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
